import React, { useEffect, useState, useRef, Suspense, lazy } from 'react';
interface LazySectionProps {
  /**
   * The section element. Should be a single lazily-imported component.
   */
  children: React.ReactNode;
  /**
   * Min-height placeholder used before the section is mounted. Prevents
   * layout shift when the chunk lands.
   */
  minHeight?: number | string;
  /**
   * Distance from the viewport (in px) at which to start fetching the chunk
   * and mounting the children. Defaults to 400px so we start streaming the
   * next section well before the user reaches it.
   */
  rootMargin?: string;
  /**
   * Optional fallback shown while the chunk is in flight. Defaults to a
   * minimal placeholder so we don't add visual noise.
   */
  fallback?: React.ReactNode;
}
/**
 * IntersectionObserver-driven lazy mount + per-section Suspense boundary.
 *
 * Each section:
 *   1. Starts as a sized placeholder (no chunk requested).
 *   2. When it scrolls within `rootMargin` of the viewport, its chunk is
 *      requested via the existing lazy import on the child.
 *   3. Its own Suspense boundary handles the in-flight state — so a slow
 *      section never blocks any other section from rendering.
 */
export function LazySection({
  children,
  minHeight = 400,
  rootMargin = '400px',
  fallback
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (visible) return;
    const node = ref.current;
    if (!node) return;
    // Fallback for environments without IntersectionObserver — mount immediately.
    if (typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      if (entries.some(e => e.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, {
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [visible, rootMargin]);
  return <div ref={ref} style={{
    minHeight: visible ? undefined : minHeight
  }} data-id="element-1142">
      {visible ? <Suspense fallback={fallback ?? <div style={{
      minHeight
    }} className="flex items-center justify-center" aria-hidden="true" data-id="element-1144">
                <div className="w-6 h-6 border-2 border-gray-700 border-t-[#FF0000] rounded-full animate-spin" data-id="element-1145" />
              </div>} data-id="element-1143">
          {children}
        </Suspense> : null}
    </div>;
}